// Helper to calculate distance between two coordinates in meters (Haversine formula)
export const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371e3; // Earth radius in metres
  const p1 = lat1 * Math.PI / 180;
  const p2 = lat2 * Math.PI / 180;
  const dp = (lat2 - lat1) * Math.PI / 180;
  const dl = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(dp / 2) * Math.sin(dp / 2) +
    Math.cos(p1) * Math.cos(p2) *
    Math.sin(dl / 2) * Math.sin(dl / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

// Helper to project a point onto a line segment
export const getClosestPointOnSegment = (p: { lat: number, lng: number }, p1: { lat: number, lng: number }, p2: { lat: number, lng: number }) => {
  const x0 = p.lng, y0 = p.lat;
  const x1 = p1.lng, y1 = p1.lat;
  const x2 = p2.lng, y2 = p2.lat;

  const dx = x2 - x1;
  const dy = y2 - y1;

  if (dx === 0 && dy === 0) {
    return { lat: y1, lng: x1 };
  }

  // Calculate t representing the projection of point on the line segment
  const t = ((x0 - x1) * dx + (y0 - y1) * dy) / (dx * dx + dy * dy);

  // Clamp t to [0, 1] to ensure the point falls on the segment
  const tClamped = Math.max(0, Math.min(1, t));

  return {
    lat: y1 + tClamped * dy,
    lng: x1 + tClamped * dx
  };
};

// Helper to find closest point on path to rider and calculate distance
export const getSplitPaths = (fullPath: google.maps.LatLng[], riderPos: { lat: number; lng: number } | null) => {
  if (!fullPath || !riderPos) return { past: [], future: fullPath || [], distanceToPath: 0, snappedPos: riderPos };

  let minDistance = Infinity;
  let closestIndex = 0;
  let snappedLocation = riderPos;
  let matchedP1 = { lat: 0, lng: 0 };
  let matchedP2 = { lat: 0, lng: 0 };

  // Project position onto each segment to find the absolute closest point on the actual route path
  for (let i = 0; i < fullPath.length - 1; i++) {
    const pt1 = fullPath[i];
    const pt2 = fullPath[i + 1];

    const p1 = typeof pt1.lat === 'function' ? { lat: pt1.lat(), lng: pt1.lng() } : { lat: (pt1 as any).lat, lng: (pt1 as any).lng };
    const p2 = typeof pt2.lat === 'function' ? { lat: pt2.lat(), lng: pt2.lng() } : { lat: (pt2 as any).lat, lng: (pt2 as any).lng };

    // Get closest point on segment
    const projected = getClosestPointOnSegment(riderPos, p1, p2);

    // Use Haversine distance for accuracy
    const dist = getDistance(projected.lat, projected.lng, riderPos.lat, riderPos.lng);

    if (dist < minDistance) {
      minDistance = dist;
      closestIndex = i;
      snappedLocation = projected;
      matchedP1 = p1;
      matchedP2 = p2;
    }
  }

  // snappedLocation is the exact point ON the route!
  const snappedLatLng = new google.maps.LatLng(snappedLocation.lat, snappedLocation.lng);

  const past = [...fullPath.slice(0, closestIndex + 1), snappedLatLng];
  const future = [snappedLatLng, ...fullPath.slice(closestIndex + 1)];

  // Calculate bearing based on the direction of the closest route segment
  let segmentBearing = 0;
  if (matchedP1 && matchedP2 && (matchedP1.lat !== matchedP2.lat || matchedP1.lng !== matchedP2.lng)) {
    const lat1 = matchedP1.lat * Math.PI / 180;
    const lat2 = matchedP2.lat * Math.PI / 180;
    const dlng = (matchedP2.lng - matchedP1.lng) * Math.PI / 180;

    const y = Math.sin(dlng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dlng);

    segmentBearing = Math.atan2(y, x) * 180 / Math.PI;
    segmentBearing = (segmentBearing + 360) % 360; // Normalize to 0-360
  }

  return { past, future, distanceToPath: minDistance, snappedPos: snappedLocation, bearing: segmentBearing };
};

// Calculate total distance (meters) along a path array
export const calculatePathDistance = (path: google.maps.LatLng[]): number => {
  let total = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const pt1 = path[i];
    const pt2 = path[i + 1];
    const p1 = typeof pt1.lat === 'function' ? { lat: pt1.lat(), lng: pt1.lng() } : { lat: (pt1 as any).lat, lng: (pt1 as any).lng };
    const p2 = typeof pt2.lat === 'function' ? { lat: pt2.lat(), lng: pt2.lng() } : { lat: (pt2 as any).lat, lng: (pt2 as any).lng };
    total += getDistance(p1.lat, p1.lng, p2.lat, p2.lng);
  }
  return total;
};
